import React from 'react';
import cx from 'classnames';
import s from './ResponsiveLayout.scss';
import {BottomPlaceholder} from '../../Placeholders/BottomPlaceholder';
import {Cell} from '../Cell/Cell';
import {DetailsPlaceholder} from '../../Placeholders/DetailsPlaceholder';
import {InfoSectionLayout} from '../../InfoSectionLayout/InfoSectionLayout';
import {LayoutComponentProps} from '../../../types/app-types';
import {Mode, WishlistButton} from '../../ProductPageButtonsContainer/WishlistButton/WishlistButton';
import {Navigation, NavigationType} from '../../Navigation/Navigation';
import {ProductDescription} from '../../ProductDescription/ProductDescription';
import {ProductName} from '../../ProductName/ProductName';
import {ProductOptions} from '../../ProductOptions/ProductOptions';
import {ProductPageButtonsContainer} from '../../ProductPageButtonsContainer/ProductPageButtonsContainer';
import {ProductSku} from '../../ProductSku/ProductSku';
import {ProductSocial} from '../../ProductSocial/ProductSocial';
import {ProductGalleryLayout} from '../../ProductGallery/ProductGalleryLayout/ProductGalleryLayout';
import {createLayoutConfigWithDefaults} from '../../ProductGallery/ProductGalleryLayout/ProductGalleryLayoutUtils';
import {ShowOnMobileOnly} from '@wix/wixstores-client-common-components/dist/es/src/HOC/responsive/ShowOnMobileOnly/ShowOnMobileOnly';
import {withGlobalProps} from '../../../providers/globalPropsProvider';
import {ProductPrice} from '../../ProductPrice/ProductPrice';
import {ProductPaymentBreakdown} from '../../ProductPaymentBreakdown/ProductPaymentBreakdown';
import {ProductDiscountName} from '../../ProductDiscountName/ProductDiscountName';
import {RatingSummarySlot} from '../../RatingSummarySlot/RatingSummarySlot';
import {ReviewsSlot} from '../../ReviewsSlot/ReviewsSlot';
import {ProductPageTopSlot} from '../../ProductPageTopSlot/ProductPageTopSlot';
import {SlotsPlaceholder} from '@wix/widget-plugins-ooi';
import {ProductPageSlotIds, Layout, Alignment} from '../../../constants';
import {ResponsiveGallery} from './ResponsiveGallery/ResponsiveGallery';
import {ConditionalRender} from '../../ConditionalRender/ConditionalRender';
import {SPECS} from '../../../specs';
import {GridGallery} from './GridGallery/GridGallery';
import stylesParams from '../../../../components/ProductPage/stylesParams';
import {StickyButtonContainer} from '../../ProductPageButtonsContainer/StickyButtonContainer/StickyButtonContainer';

class ResponsiveLayoutComponent extends React.Component<LayoutComponentProps> {
  private renderResponsiveGallery() {
    const {
      settings,
      globals: {experiments, product},
    } = this.props;
    const {responsiveGalleryMigration} = experiments ?? {};

    /* istanbul ignore next */
    return responsiveGalleryMigration ? (
      <ProductGalleryLayout
        key={product.media.length > 0 ? product.media[0].id : 'no-media'}
        layoutConfig={createLayoutConfigWithDefaults({
          align: Alignment.START,
          withImageRatio: true,
          dimensions: {
            mainMedia: {
              widthConf: {num: 0, unit: 'px'},
              heightConf: {num: 0, unit: 'px'},
            },
            thumbnails: {
              widthConf: {num: 0, unit: 'px'},
              heightConf: {num: 50, unit: 'px'},
            },
          },
        })}
        layout={Layout.Responsive}
        imageMode={settings.imageMode}
        imageRatioId={settings.imageRatioId}
        navigationType={settings.navigationType}
        navigationPosition={settings.navigationPosition}
        navigationLocation={settings.navigationLocation}
      />
    ) : (
      <ResponsiveGallery
        key={product.media.length > 0 ? product.media[0].id : 'no-media'}
        media={product.media}
        productName={product.name}
        imageMode={settings.imageMode}
        imageRatio={settings.imageRatioId}
        navigationType={settings.navigationType}
        navigationPosition={settings.navigationPosition}
      />
    );
  }

  private renderLeftCol() {
    const {
      settings,
      globals: {useExperiments, shouldShowWishlistButton, product, slots, experiments},
    } = this.props;
    const {renderProductPageSlots} = experiments ?? {};
    const isMediaPluginInstalled = !!slots?.[ProductPageSlotIds.ProductPageMedia1];
    const isProductPageSupportGridLayoutEnabled = useExperiments.enabled(SPECS.ProductPageSupportGridLayout);

    const shouldRenderFloatingWishlistButton =
      (!renderProductPageSlots && shouldShowWishlistButton) ||
      (renderProductPageSlots && !isMediaPluginInstalled && shouldShowWishlistButton);

    return (
      <section className={cx(s.col, s.mainColumn, s.stickyColumnMedia)}>
        <Cell className={s.gallery}>
          {isProductPageSupportGridLayoutEnabled ? (
            <>
              <ConditionalRender by="shouldRenderGridLayout">
                <GridGallery
                  key={
                    product.media.length > 0 ? product.media[0].id : /* istanbul ignore next hard to test */ 'no-media'
                  }
                  media={product.media}
                  productName={product.name}
                  imageMode={settings.imageMode}
                />
              </ConditionalRender>
              <ConditionalRender by="shouldRenderMainImageLayout">{this.renderResponsiveGallery()}</ConditionalRender>
            </>
          ) : (
            this.renderResponsiveGallery()
          )}
          {renderProductPageSlots && isMediaPluginInstalled && (
            <div className={s.slotAndWishlistButtonContainer} data-hook="slotAndWishlistButtonContainer">
              <div className={s.slotAndWishlistButtonContainerInner}>
                {shouldShowWishlistButton && (
                  <ShowOnMobileOnly>
                    <WishlistButton mode={Mode.WITH_SLOT} />
                  </ShowOnMobileOnly>
                )}
              </div>
            </div>
          )}
          {shouldRenderFloatingWishlistButton && (
            <ShowOnMobileOnly>
              <WishlistButton mode={Mode.FLOATING} />
            </ShowOnMobileOnly>
          )}
        </Cell>
      </section>
    );
  }

  private renderRightCol() {
    const {globals} = this.props;
    /* istanbul ignore next: todo: test */
    const {renderProductPageSlots} = globals.experiments ?? {};
    const {product, hasDiscountName, hasPaymentOptionsBreakdown, useExperiments} = globals;
    const isPluginInstalledInDetails1Slot = !!globals.slots?.[ProductPageSlotIds.ProductPageDetails1];
    const isPluginInstalledInDetails2Slot = !!globals.slots?.[ProductPageSlotIds.ProductPageDetails2];
    const isPluginInstalledInDetails3Slot = !!globals.slots?.[ProductPageSlotIds.ProductPageDetails3];
    const isPluginInstalledInDetails4Slot = !!globals.slots?.[ProductPageSlotIds.ProductPageDetails4];
    const isPluginInstalledInDetails8Slot = !!globals.slots?.[ProductPageSlotIds.ProductPageDetails8];
    const isPluginInstalledInDetails9Slot = !!globals.slots?.[ProductPageSlotIds.ProductPageDetails9];
    const productPageDescriptionToggle = useExperiments.enabled(SPECS.ProductPageDescriptionToggle);

    return (
      <section className={cx(s.col, s.secondaryColumn, s.stickyColumnInfo)}>
        <ConditionalRender by="productPage_productSku">
          <Cell className={s.sku}>
            <ProductSku />
          </Cell>
        </ConditionalRender>
        {renderProductPageSlots && (
          <Cell className={cx({[s.slotCell]: isPluginInstalledInDetails2Slot}, s.smallGap)}>
            <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails2} />
          </Cell>
        )}
        <Cell className={s.name}>
          <ProductName name={product.name} />
        </Cell>
        {renderProductPageSlots && (
          <Cell className={cx({[s.slotCell]: isPluginInstalledInDetails1Slot}, s.smallGap)}>
            <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails1} />
          </Cell>
        )}
        <RatingSummarySlot className={cx(s.slotCell, s.smallGap)} />
        <ConditionalRender by="productPage_productPrice">
          <Cell className={s.price}>
            <ProductPrice />
          </Cell>
        </ConditionalRender>
        {renderProductPageSlots && (
          <Cell className={cx({[s.slotCell]: isPluginInstalledInDetails3Slot})}>
            <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails3} />
          </Cell>
        )}

        {hasDiscountName && (
          <ConditionalRender by="productPage_showDiscountName">
            <Cell>
              <ProductDiscountName name={product.itemDiscount.discountRuleName} />
            </Cell>
          </ConditionalRender>
        )}

        {hasPaymentOptionsBreakdown && (
          <ConditionalRender by="productPage_paymentOptionsBreakdown">
            <Cell>
              <ProductPaymentBreakdown />
            </Cell>
          </ConditionalRender>
        )}

        <Cell>
          <DetailsPlaceholder />
        </Cell>
        <Cell className={s.optionsAndButtonsContainer}>
          <ProductOptions className={s.options} />
          <ProductPageButtonsContainer />
        </Cell>
        {productPageDescriptionToggle ? (
          <ConditionalRender by="productPage_productDescription">
            <Cell className={s.description}>
              <ProductDescription description={product.description} />
            </Cell>
          </ConditionalRender>
        ) : (
          <Cell className={s.description}>
            <ProductDescription description={product.description} />
          </Cell>
        )}
        {renderProductPageSlots && (
          <Cell className={cx({[s.slotCell]: isPluginInstalledInDetails4Slot})}>
            <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails4} />
          </Cell>
        )}
        {renderProductPageSlots && (
          <Cell className={cx({[s.slotCell]: isPluginInstalledInDetails8Slot})}>
            <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails8} />
          </Cell>
        )}
        <ConditionalRender by="productPage_productInfoSection">
          <Cell className={s.info}>
            <InfoSectionLayout />
          </Cell>
        </ConditionalRender>
        {renderProductPageSlots && (
          <Cell className={cx({[s.slotCell]: isPluginInstalledInDetails9Slot})}>
            <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails9} />
          </Cell>
        )}
        <ConditionalRender by="productPage_socialNetworks">
          <Cell className={s.social}>
            <ProductSocial />
          </Cell>
        </ConditionalRender>
      </section>
    );
  }

  public render(): JSX.Element {
    const {isMobile, slots, isResponsive, useExperiments, styles} = this.props.globals;
    const {renderProductPageSlots} = this.props.globals.experiments ?? {};
    const navigationType = isMobile ? NavigationType.Short : NavigationType.Long;
    const isPluginInstalledInTopSlot = !!slots?.[ProductPageSlotIds.ProductPageTop];
    const isPluginInstalledInBottomSlot = !!slots?.[ProductPageSlotIds.ProductPageBottom];
    const isResponsiveEditorBreadcrumbsToggleEnabled = useExperiments.enabled(SPECS.ResponsiveEditorBreadcrumbsToggle);
    const isResponsiveEditor = styles.get(stylesParams.responsive);
    const isNavigationEnabled = styles.get(stylesParams.productPage_navigation);
    const isStickyContainerEnabled = styles.get(stylesParams.productPage_stickyContainerEnabled);
    const shouldRenderStickyButton =
      isMobile && useExperiments.enabled(SPECS.StickyAddToCartMobile) && isStickyContainerEnabled;

    const isRenderBreadcrumbs =
      isResponsiveEditorBreadcrumbsToggleEnabled && isResponsiveEditor ? isNavigationEnabled : isMobile || isResponsive;

    return (
      <article className={s.container}>
        <div className={cx({[s.topSlot]: isPluginInstalledInTopSlot})}>
          <ProductPageTopSlot />
        </div>
        <ConditionalRender by="shouldRenderResponsiveLayoutNavigation">
          <Navigation className={s.navigation} renderBreadcrumbs={isRenderBreadcrumbs} type={navigationType} />
        </ConditionalRender>
        <div className={cx(s.main)}>
          {this.renderLeftCol()}
          {this.renderRightCol()}
        </div>
        {renderProductPageSlots && (
          <Cell className={cx({[s.slotCell]: isPluginInstalledInBottomSlot})}>
            <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageBottom} />
          </Cell>
        )}
        <Cell>
          <ReviewsSlot />
          <BottomPlaceholder />
        </Cell>
        {shouldRenderStickyButton && <StickyButtonContainer />}
      </article>
    );
  }
}

export const ResponsiveLayout = withGlobalProps(ResponsiveLayoutComponent);
